import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Heartbeat interval in milliseconds (20 seconds)
const HEARTBEAT_INTERVAL = 20000;

/**
 * Custom hook for managing job card locks/presence
 * 
 * @param {string} jobId - The job ID to lock
 * @param {string} screen - The screen name (e.g., 'inspection', 'pricing')
 * @param {boolean} enabled - Whether to enable locking (default: true)
 * @returns {object} - Lock state and controls
 */
export function useJobLock(jobId, screen = 'general', enabled = true) {
  const [lockStatus, setLockStatus] = useState({
    isLocked: false,
    isCurrentUser: false,
    lockedByName: null,
    lockedAt: null,
    warning: null,
    loading: true
  });
  
  const heartbeatIntervalRef = useRef(null);
  const isActiveRef = useRef(false);

  // Acquire lock
  const acquireLock = useCallback(async () => {
    if (!jobId || !enabled) return;
    
    try {
      const res = await axios.post(`${API}/job/${jobId}/lock`, {
        job_id: jobId,
        screen: screen
      });
      
      const { lock_acquired, warning, lock } = res.data;
      
      setLockStatus({
        isLocked: !lock_acquired,
        isCurrentUser: lock_acquired,
        lockedByName: lock?.locked_by_name || null,
        lockedAt: lock?.locked_at || null,
        warning: warning || null,
        loading: false
      });
      
      if (lock_acquired) {
        isActiveRef.current = true;
        startHeartbeat();
      }
      
      return res.data;
    } catch (err) {
      console.error('Failed to acquire lock:', err);
      setLockStatus(prev => ({ ...prev, loading: false }));
      return null;
    }
  }, [jobId, screen, enabled]);

  // Release lock
  const releaseLock = useCallback(async () => {
    if (!jobId || !isActiveRef.current) return;
    
    stopHeartbeat();
    isActiveRef.current = false;
    
    try {
      await axios.delete(`${API}/job/${jobId}/lock`);
    } catch (err) {
      console.error('Failed to release lock:', err);
    }
  }, [jobId]);

  // Send heartbeat
  const sendHeartbeat = useCallback(async () => {
    if (!jobId || !isActiveRef.current) return;
    
    try {
      const res = await axios.post(`${API}/job/${jobId}/heartbeat`);
      
      if (!res.data.success) {
        // Lost the lock
        setLockStatus(prev => ({
          ...prev,
          isLocked: true,
          isCurrentUser: false,
          warning: res.data.message || 'Lock lost'
        }));
        stopHeartbeat();
        isActiveRef.current = false;
      }
    } catch (err) {
      console.error('Heartbeat failed:', err);
    }
  }, [jobId]);

  // Start heartbeat interval
  const startHeartbeat = useCallback(() => {
    if (heartbeatIntervalRef.current) {
      clearInterval(heartbeatIntervalRef.current);
    }
    heartbeatIntervalRef.current = setInterval(sendHeartbeat, HEARTBEAT_INTERVAL);
  }, [sendHeartbeat]);

  // Stop heartbeat interval
  const stopHeartbeat = useCallback(() => {
    if (heartbeatIntervalRef.current) {
      clearInterval(heartbeatIntervalRef.current);
      heartbeatIntervalRef.current = null;
    }
  }, []);

  // Check lock status without acquiring
  const checkLockStatus = useCallback(async () => {
    if (!jobId) return null;
    
    try {
      const res = await axios.get(`${API}/job/${jobId}/lock-status`);
      return res.data;
    } catch (err) {
      console.error('Failed to check lock status:', err);
      return null;
    }
  }, [jobId]);

  // Acquire lock on mount, release on unmount
  useEffect(() => {
    if (enabled && jobId) {
      acquireLock();
    }
    
    return () => {
      releaseLock();
    };
  }, [jobId, enabled]);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      stopHeartbeat();
    };
  }, [stopHeartbeat]);

  // Handle page visibility changes
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        // Page is hidden - release lock
        releaseLock();
      } else if (document.visibilityState === 'visible' && enabled && jobId) {
        // Page is visible again - reacquire lock
        acquireLock();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [jobId, enabled, acquireLock, releaseLock]);

  // Handle beforeunload
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (isActiveRef.current && jobId) {
        // Synchronous release attempt
        navigator.sendBeacon(`${API}/job/${jobId}/lock/release-beacon`);
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [jobId]);

  return {
    ...lockStatus,
    acquireLock,
    releaseLock,
    checkLockStatus,
    isActive: isActiveRef.current
  };
}

/**
 * Hook to get all active locks for displaying on job lists
 */
export function useActiveLocks() {
  const [locks, setLocks] = useState({});
  const [loading, setLoading] = useState(true);

  const fetchLocks = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/job-locks/active`);
      const locksMap = {};
      res.data.locks.forEach(lock => {
        locksMap[lock.job_id] = lock;
      });
      setLocks(locksMap);
    } catch (err) {
      console.error('Failed to fetch active locks:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLocks();
    // Refresh every 30 seconds
    const interval = setInterval(fetchLocks, 30000);
    return () => clearInterval(interval);
  }, [fetchLocks]);

  return { locks, loading, refresh: fetchLocks };
}

export default useJobLock;
